import { Button, message } from 'antd';
import { useState, useRef, useCallback, startTransition, memo } from 'react';
import {
	LuPlus,
	LuSettings,
	LuDownload,
	LuUpload,
	LuUndo,
	LuRedo,
	LuEye,
	LuPencilLine,
} from 'react-icons/lu';
import { uploadToS3 } from 'src/_shared/services/utils.js';

function Toolbar({
	config,
	onConfigChange,
	onAddCard,
	cards,
	onCardsChange,
	onUndo,
	onRedo,
	canUndo,
	canRedo,
	isEditMode,
	onToggleEditMode,
	currentUser,
	setCurrentUser,
}) {
	const [showSettings, setShowSettings] = useState(false);
	const [isSavingChanges, setIsSavingChanges] = useState(false);
	const fileInputRef = useRef(null);

	const updateConfig = useCallback(
		(updates) => {
			startTransition(() => {
				onConfigChange({ ...config, ...updates });
			});
		},
		[config, onConfigChange]
	);

	const updateBreakpoint = useCallback(
		(index, updates) => {
			const next = [...config.breakpoints];
			next[index] = { ...next[index], ...updates };
			onConfigChange({ ...config, breakpoints: next }, true);
		},
		[config, onConfigChange]
	);

	const handleExport = useCallback(() => {
		const exportData = { config, cards };
		const blob = new Blob([JSON.stringify(exportData, null, 2)], {
			type: 'application/json',
		});
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = `dashboard-layout-${new Date().toISOString().split('T')[0]}.json`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	}, [config, cards]);

	const handlePublish = useCallback(async () => {
		if (isSavingChanges) return;
		setIsSavingChanges(true);
		try {
			const publishData = { config, cards };
			const jsonBlob = new Blob([JSON.stringify(publishData, null, 2)], {
				type: 'application/json',
			});

			await uploadToS3(
				jsonBlob,
				`dashboard/${currentUser.companyId}/dashboard-layout.json`,
				'customizable-ui'
			);

			setCurrentUser({
				...currentUser,
				company: {
					...currentUser.company,
					customDashboard: JSON.stringify(publishData),
				},
			});

			message.success('Dashboard changes published successfully');
		} catch (error) {
			console.error('Error publishing dashboard changes:', error);
			message.error('Failed to publish dashboard changes');
		}
		setIsSavingChanges(false);
	}, [config, cards, currentUser]);

	const handleImport = useCallback(() => fileInputRef.current?.click(), []);

	const handleFileChange = useCallback(
		(event) => {
			const file = event.target.files?.[0];
			if (!file) return;

			const reader = new FileReader();
			reader.onload = (e) => {
				try {
					const content = String(e.target?.result ?? '');
					const importData = JSON.parse(content);

					if (!importData?.config || !Array.isArray(importData?.cards)) {
						alert('Invalid file format. Expected { config, cards[] }.');
						return;
					}
					const required = [
						'horizontalMargin',
						'verticalMargin',
						'rowHeight',
						'columnWidth',
						'breakpoints',
						'responsiveWidth',
					];
					const hasAll = required.every((k) => k in importData.config);
					if (!hasAll) {
						alert('Invalid config. Missing required properties.');
						return;
					}
					const validCards = importData.cards.every(
						(c) =>
							typeof c?.id === 'string' &&
							Number.isFinite(c?.x) &&
							Number.isFinite(c?.y) &&
							Number.isFinite(c?.width) &&
							Number.isFinite(c?.height)
					);
					if (!validCards) {
						alert('Invalid cards. Each card needs id, x, y, width, height.');
						return;
					}

					onConfigChange(importData.config, true);
					onCardsChange(importData.cards, true);
				} catch {
					alert('Failed to parse JSON file. Please ensure it is valid JSON.');
				}
			};
			reader.readAsText(file);
			event.target.value = '';
		},
		[onConfigChange, onCardsChange]
	);

	const toggleSettings = useCallback(() => setShowSettings((s) => !s), []);

	const parseNum = (v, fallback = 0) => {
		const n = Number.parseInt(v, 10);
		return Number.isFinite(n) ? n : fallback;
	};

	return (
		<div className="">
			<div className="px-3">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-4">
						<button
							onClick={onToggleEditMode}
							className={`flex items-center gap-2 px-3 py-3 transition-colors shadow-sm ${
								isEditMode
									? 'bg-green-600 text-white hover:bg-green-700'
									: 'bg-blue-600 text-white hover:bg-blue-700'
							}`}
							style={{ borderRadius: '50%' }}
						>
							{isEditMode ? (
								<>
									<LuEye size={18} />
								</>
							) : (
								<>
									<LuPencilLine size={18} />
								</>
							)}
						</button>

						{isEditMode && (
							<button
								onClick={onAddCard}
								className="flex items-center gap-2 px-3 py-3 bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-sm"
								style={{ borderRadius: '50%' }}
							>
								<LuPlus size={18} />
							</button>
						)}
					</div>

					<div className="flex items-center gap-3">
						{isEditMode && (
							<>
								<Button
									onClick={handlePublish}
									title="Publish Changes"
									loading={isSavingChanges}
								>
									Publish Changes
								</Button>
								<button
									onClick={onUndo}
									disabled={!canUndo}
									className="flex items-center gap-2 px-3 py-3 bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-200"
									title="Undo"
									style={{ borderRadius: '50%' }}
								>
									<LuUndo size={18} />
								</button>

								<button
									onClick={onRedo}
									disabled={!canRedo}
									className="flex items-center gap-2 px-3 py-3 bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-200"
									title="Redo"
									style={{ borderRadius: '50%' }}
								>
									<LuRedo size={18} />
								</button>

								<div className="w-px h-8 bg-gray-300"></div>
							</>
						)}

						<button
							onClick={handleExport}
							className="flex items-center gap-2 px-3 py-3 bg-green-600 text-white hover:bg-green-700 transition-colors shadow-sm"
							title="Export layout"
							style={{ borderRadius: '50%' }}
						>
							<LuDownload size={18} />
						</button>

						{isEditMode && (
							<>
								<button
									onClick={handleImport}
									className="flex items-center gap-2 px-3 py-3 bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-sm"
									title="Import layout"
									style={{ borderRadius: '50%' }}
								>
									<LuUpload size={18} />
								</button>

								<input
									ref={fileInputRef}
									type="file"
									accept="application/json,.json"
									onChange={handleFileChange}
									className="hidden"
								/>

								<button
									onClick={toggleSettings}
									className="flex items-center gap-2 px-3 py-3 bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
									style={{ borderRadius: '50%' }}
								>
									<LuSettings size={18} />
								</button>
							</>
						)}
					</div>
				</div>

				{showSettings && (
					<div className="mt-6 pt-6 border-t border-gray-200">
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">
									Horizontal Margin (px)
								</label>
								<input
									type="number"
									value={config.horizontalMargin}
									onChange={(e) =>
										updateConfig({
											horizontalMargin: parseNum(e.target.value, 0),
										})
									}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
									min="0"
								/>
							</div>

							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">
									Vertical Margin (px)
								</label>
								<input
									type="number"
									value={config.verticalMargin}
									onChange={(e) =>
										updateConfig({
											verticalMargin: parseNum(e.target.value, 0),
										})
									}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
									min="0"
								/>
							</div>

							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">
									Row Height (px)
								</label>
								<input
									type="number"
									value={config.rowHeight}
									onChange={(e) =>
										updateConfig({ rowHeight: parseNum(e.target.value, 10) })
									}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
									min="10"
								/>
							</div>

							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">
									Column Width (px)
								</label>
								<input
									type="number"
									value={config.columnWidth}
									onChange={(e) =>
										updateConfig({ columnWidth: parseNum(e.target.value, 10) })
									}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
									min="10"
									disabled={config.responsiveWidth}
								/>
							</div>
						</div>

						<div className="mt-4">
							<label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
								<input
									type="checkbox"
									checked={config.responsiveWidth}
									onChange={(e) =>
										updateConfig({ responsiveWidth: e.target.checked })
									}
									className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
								/>
								Responsive Width Mode
							</label>
							<p className="mt-1 text-xs text-gray-500 ml-6">
								Card widths automatically adjust to fill the container width
								based on the number of columns
							</p>
						</div>

						<div className="mt-4">
							<label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
								<input
									type="checkbox"
									checked={config.enableDashboardSearch}
									onChange={(e) =>
										updateConfig({ enableDashboardSearch: e.target.checked })
									}
									className="w-4 h-4 mr-2 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
								/>
								Enable Dashboard Job Search
							</label>
							<p className="mt-1 text-xs text-gray-500 ml-6">
								Displays the Employee Dashboard Job Search at the top of the
								page. Users will be taken to the Jobs page to browse the results
								of their search term. Supports a custom background image.
							</p>
						</div>

						<div className="mt-6">
							<h3 className="text-sm font-medium text-gray-900 mb-4">
								Breakpoints
							</h3>
							<div className="space-y-4">
								{config.breakpoints.map((bp, index) => (
									<div key={bp.name} className="flex items-center gap-4">
										<div className="flex-1">
											<label className="block text-xs font-medium text-gray-600 mb-1">
												{bp.name}
											</label>
											<div className="flex gap-3">
												<div className="flex-1">
													<input
														type="number"
														value={bp.minWidth}
														onChange={(e) =>
															updateBreakpoint(index, {
																minWidth: parseNum(e.target.value, 0),
															})
														}
														placeholder="Min Width (px)"
														className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
														min="0"
													/>
												</div>
												<div className="flex-1">
													<input
														type="number"
														value={bp.columns}
														onChange={(e) =>
															updateBreakpoint(index, {
																columns: parseNum(e.target.value, 1),
															})
														}
														placeholder="Columns"
														className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
														min="1"
														max="12"
													/>
												</div>
											</div>
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}

export default memo(Toolbar);
